import * as THREE from 'three';
import {
  ISLAND_FIELD,
  islandProps,
  islandsInRadius,
  type IslandCell,
  type PropPlacement,
} from '../../shared/islandField';
import { ModelLibrary } from '../entities/ModelLibrary';
import { buildIslandTerrain } from '../entities/IslandTerrain';
import { buildIslandProps } from '../entities/IslandProps';

/**
 * Client world streaming for islands.
 *
 * The world is the shared `islandField` hash grid; most cells are empty and a
 * few hold one island. This system keeps a sparse set of island groups alive
 * around the ship: islands inside `planeSize/2 + maxRadius + margin` are built,
 * everything outside (with hysteresis) is disposed. Ground is a vertex-colour
 * heightfield + foam ring (`IslandTerrain`), props are instanced per type via
 * `ModelLibrary.instance` (`IslandProps`), so a whole island is only a handful
 * of draw calls.
 *
 * The orchestrator owns `Game.ts`; this class is the single integration point:
 * construct it once with the scene + model library, call `update(position)` each
 * frame with the ship position, and call `dispose()` on shutdown.
 */

export interface WorldStreamerOptions {
  /** Mobile profile tightens the radius, LOD and prop caps for the mobile budget. */
  mobile: boolean;
  /** Streaming radius override; defaults to planeSize/2 + maxRadius + margin. */
  activationRadius?: number;
  /** Maximum simultaneous active islands (default 2 mobile, 4 desktop). */
  maxIslands?: number;
  /** Build rock/palm props; set false to save draw calls (mobile). */
  props?: boolean;
}

interface ActiveIsland {
  cell: IslandCell;
  group: THREE.Group;
  /** Deterministic prop placements, derived once when the island activates. */
  placements: PropPlacement[];
  propsBuilt: boolean;
  /** Retry throttle so prop building doesn't re-run every frame while models load. */
  retryTick: number;
}

const PROP_RETRY_INTERVAL = 10;

export class WorldStreamer {
  private readonly scene: THREE.Scene;
  private readonly modelLibrary: ModelLibrary;
  private readonly mobile: boolean;
  private readonly activationRadius: number;
  private readonly deactivationRadius: number;
  private readonly maxIslands: number;
  private readonly propsEnabled: boolean;
  private readonly terrainMaterial: THREE.MeshStandardMaterial;
  private readonly foamMaterial: THREE.MeshBasicMaterial;
  private readonly active = new Map<string, ActiveIsland>();

  constructor(
    scene: THREE.Scene,
    modelLibrary: ModelLibrary,
    options: WorldStreamerOptions,
  ) {
    this.scene = scene;
    this.modelLibrary = modelLibrary;
    this.mobile = options.mobile;
    this.maxIslands = options.maxIslands ?? (options.mobile ? 2 : 4);
    this.propsEnabled = options.props ?? true;

    const planeSize = options.mobile ? 420 : 760;
    const margin = options.mobile ? 30 : 60;
    const base =
      options.activationRadius ?? planeSize / 2 + ISLAND_FIELD.maxRadius + margin;
    this.activationRadius = base;
    // Hysteresis: an island riding the boundary doesn't build/dispose every frame.
    this.deactivationRadius = base * 1.15;

    this.terrainMaterial = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 1,
      metalness: 0,
    });
    this.foamMaterial = new THREE.MeshBasicMaterial({
      color: 0xd9edf5,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.8,
      depthWrite: false,
    });
  }

  /**
   * Stream islands around `position`. Call once per frame with the ship pose.
   * Builds any newly-near islands, unloads islands that drifted outside the
   * (hysteresis-widened) radius, and tops up prop instancing while models load.
   */
  update(position: THREE.Vector3 | { x: number; z: number }): void {
    const x = position.x;
    const z = position.z;
    const nearby = islandsInRadius(x, z, this.activationRadius);
    const wanted = nearby.slice(0, this.maxIslands);
    const wantedIds = new Set(wanted.map((cell) => cell.id));
    const overCap = nearby.length > this.maxIslands;

    for (const [id, island] of this.active) {
      if (wantedIds.has(id)) continue;
      const distance = Math.hypot(x - island.cell.x, z - island.cell.z);
      // Over the cap the farthest extras unload immediately; otherwise only past
      // the hysteresis radius so a boundary island doesn't churn.
      if (distance > this.deactivationRadius || overCap) this.deactivate(id);
    }

    for (const cell of wanted) {
      if (!this.active.has(cell.id)) this.activate(cell);
    }

    for (const island of this.active.values()) {
      if (!this.propsEnabled || island.propsBuilt) continue;
      island.retryTick += 1;
      if (island.retryTick % PROP_RETRY_INTERVAL === 0) {
        const result = buildIslandProps(
          island.cell,
          island.placements,
          this.modelLibrary,
          island.group,
          { castShadow: true },
        );
        island.propsBuilt = result.totalTypes === 0 || result.builtTypes === result.totalTypes;
      }
    }
  }

  /** Number of islands currently built and rendered (diagnostics). */
  get islandCount(): number {
    return this.active.size;
  }

  /** Dispose all islands and the shared ground/foam materials. */
  dispose(): void {
    for (const id of [...this.active.keys()]) this.deactivate(id);
    this.terrainMaterial.dispose();
    this.foamMaterial.dispose();
  }

  private activate(cell: IslandCell): void {
    const group = new THREE.Group();
    group.name = `island-${cell.id}`;
    group.position.set(cell.x, 0, cell.z);

    const terrain = buildIslandTerrain(cell, {
      mobile: this.mobile,
      material: this.terrainMaterial,
      foamMaterial: this.foamMaterial,
    });
    group.add(terrain.lod);
    group.add(terrain.foam);
    this.scene.add(group);

    const island: ActiveIsland = {
      cell,
      group,
      placements: islandProps(cell),
      propsBuilt: false,
      retryTick: 0,
    };
    this.active.set(cell.id, island);

    if (this.propsEnabled) {
      const result = buildIslandProps(cell, island.placements, this.modelLibrary, group, {
        castShadow: true,
      });
      island.propsBuilt = result.totalTypes === 0 || result.builtTypes === result.totalTypes;
    } else {
      island.propsBuilt = true;
    }
  }

  private deactivate(id: string): void {
    const island = this.active.get(id);
    if (!island) return;
    this.scene.remove(island.group);
    island.group.traverse((object) => {
      if ((object as THREE.InstancedMesh).isInstancedMesh) {
        // Instanced props share the model library's cached geometry/material;
        // only the per-instance matrix storage is owned here.
        (object as THREE.InstancedMesh).dispose();
        return;
      }
      const mesh = object as THREE.Mesh;
      if (mesh.isMesh) {
        // Ground/foam meshes own their geometry but share the streamer materials.
        mesh.geometry?.dispose();
      }
    });
    this.active.delete(id);
  }
}
